import { Card, Col, Form, FormInstance, Input, InputNumber, Row } from 'antd'
import { useEffect, useState } from 'react'

import {
  GetSubstanceListResponseItem,
  ProductGroupCategory
} from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import {
  InternationalNamesSelect,
  ProducersSelect,
  StockGroups
} from 'src/components'
import { useLocales } from 'src/locales'

import { PharmaceuticalFormsSelect } from './PharmaceuticalFormsSelect'
import { PrescriptionsSelect } from './PrescriptionsSelect'

type Props = {
  form: FormInstance
  hasDose?: boolean
}

export const ProductFields = ({ form, hasDose }: Props) => {
  const { t } = useLocales()

  const [selectedInternationalName, setSelectedInternationalName] = useState<
    GetSubstanceListResponseItem | undefined
  >()
  const [isDosesDisabled, setIsDosesDisabled] = useState(false)

  useEffect(() => {
    if (selectedInternationalName) {
      form.setFieldsValue({
        doses: selectedInternationalName.dosage,
        internationalNameId: selectedInternationalName.id
      })
      setIsDosesDisabled(!!selectedInternationalName.dosage)
    } else {
      !hasDose && form.setFieldsValue({ doses: undefined })
      setIsDosesDisabled(!!hasDose)
    }
  }, [selectedInternationalName, hasDose])

  return (
    <Card>
      <Row gutter={[24, 24]}>
        <Col span={24} xl={12}>
          <Form.Item name="code" rules={requiredRules} label={t('common.code')}>
            <Input placeholder={t('common.code')} />
          </Form.Item>
        </Col>

        <Col span={24} xl={12}>
          <Form.Item
            name="commercialName"
            rules={requiredRules}
            label={t('medicine_register.products.name')}
          >
            <Input placeholder={t('medicine_register.products.name')} />
          </Form.Item>
        </Col>

        <Col span={24} xl={12}>
          <Form.Item
            name="internationalNameId"
            rules={requiredRules}
            label={t('medicine_register.international_name.title')}
          >
            <InternationalNamesSelect
              placeholder={t('medicine_register.international_name.title')}
              setInternationalName={setSelectedInternationalName}
            />
          </Form.Item>
        </Col>

        <Col span={24} xl={12}>
          <Form.Item
            name="producerId"
            rules={requiredRules}
            label={t('medicine_register.producers.title')}
          >
            <ProducersSelect
              placeholder={t('medicine_register.producers.title')}
            />
          </Form.Item>
        </Col>

        <Col span={24} xl={12}>
          <Form.Item
            name="formId"
            rules={requiredRules}
            label={t('medicine_register.pharmaceutical_form.title')}
          >
            <PharmaceuticalFormsSelect
              placeholder={t('medicine_register.pharmaceutical_form.title')}
            />
          </Form.Item>
        </Col>

        <Col span={24} xl={12}>
          <Form.Item
            name="prescriptionId"
            rules={requiredRules}
            label={t('medicine_register.products.prescription')}
          >
            <PrescriptionsSelect
              placeholder={t('medicine_register.products.prescription')}
            />
          </Form.Item>
        </Col>

        <Col span={24} xl={12}>
          <Form.Item
            name="validityPeriod"
            label={t('medicine_register.products.validity_period')}
          >
            <InputNumber
              className="full-width"
              min={1}
              placeholder={t('medicine_register.products.validity_period')}
            />
          </Form.Item>
        </Col>

        <Col span={24} xl={12}>
          <Form.Item
            name="groupId"
            rules={requiredRules}
            label={t('common.group')}
          >
            <StockGroups
              category={ProductGroupCategory.MEDICINES}
              labelInValue
            />
          </Form.Item>
        </Col>

        <Col span={24} xl={12}>
          <Form.Item
            name="doses"
            rules={requiredRules}
            label={t('medicine_register.products.dose')}
          >
            <Input placeholder="35 mg" disabled={isDosesDisabled} />
          </Form.Item>
        </Col>

        <Col span={24} xl={12}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="division"
                rules={requiredRules}
                label={t('medicine_register.products.division')}
              >
                <Input placeholder={t('medicine_register.products.division')} />
              </Form.Item>
            </Col>

            <Col span={7}>
              <Form.Item
                name="unitSize"
                label={<></>}
                rules={[{ required: true, message: '' }]}
              >
                <InputNumber
                  style={{ width: 'auto' }}
                  min={0}
                  placeholder="10"
                />
              </Form.Item>
            </Col>

            <Col span={5}>
              <Form.Item
                name="unitsCount"
                label={<></>}
                rules={[{ required: true, message: '' }]}
              >
                <InputNumber
                  style={{ width: 'auto' }}
                  min={0}
                  placeholder="3"
                  prefix="x"
                />
              </Form.Item>
            </Col>
          </Row>
        </Col>

        <Col span={24} xl={12}>
          <Form.Item
            name="volume"
            label={t('medicine_register.products.volume')}
          >
            <Input placeholder="100 ml" />
          </Form.Item>
        </Col>
      </Row>
    </Card>
  )
}
